// The matchup cue on a row (PLAN-2026-10-03-research-tools.md §4, B-249):
// how a player's opponent has done against his position this season, in this
// league's scoring, said the way B-218 says it — "3rd most", "2nd fewest" —
// and tinted by which third of the league that is in.
//
// THE TINT IS NEVER ALONE. It is the figure's heat, beside the figure in
// words, so it reads without color and never says what to do: no "tough", no
// "easy", no "start" (docs/decisions/2026-09-12-tools-not-instructions.md). A
// cue resting on fewer than three games is drawn dashed and says it is early.
//
// Standard and up, where the opponent is shown (DETAIL_LEVELS.md). The
// lineup's rows show it before kickoff, which is when a manager is deciding;
// Players' opponent column whatever the game's state (B-520).

import { MATCHUP_EARLY_GAMES, type MatchupCue } from '@fsp/domain';
import { useAtLeast } from '../../data/detail';
import { ordinal } from './RosterMoveSheet';
import { Term } from '../../kit/Why';
import { clubLabel, positionLabelOf } from '../../sports/registry';

/** "2nd fewest" or "4th most", whichever half of the table the rank is in (B-218). */
export function rankWords(rank: number, of: number): string {
  return rank <= Math.ceil(of / 2) ? `${ordinal(rank)} most` : `${ordinal(of - rank + 1)} fewest`;
}

/** "MIN has allowed 24.1 a game to RB, the 3rd most of 32, over 5 games, in this league's scoring." */
export function matchupSentence(
  cue: MatchupCue,
  position: string | null,
  opponent: string | null,
): string {
  const early = cue.games < MATCHUP_EARLY_GAMES;
  return (
    `${opponent === null ? 'This opponent' : clubLabel(opponent)} has allowed ${cue.perGame.toFixed(1)} a game to ` +
    `${position === null ? 'this position' : positionLabelOf(position)}, the ${rankWords(cue.rank, cue.of)} of ${cue.of}, over ` +
    `${cue.games} ${cue.games === 1 ? 'game' : 'games'}${early ? ', which is early' : ''}, ` +
    `in this league's scoring.`
  );
}

export function MatchupChip({
  cue,
  position,
  opponent,
  always = false,
}: {
  cue: MatchupCue | null | undefined;
  position: string | null | undefined;
  opponent: string | null | undefined;
  /** Shown whatever the detail level, where the screen is already at its numbers. */
  always?: boolean;
}) {
  const standard = useAtLeast('standard');
  if (!cue || (!standard && !always)) return null;
  const sentence = matchupSentence(cue, position ?? null, opponent ?? null);
  // THE SENTENCE ON A TAP AS WELL AS A HOVER: it was a native title, which a
  // phone never shows, so "3rd fewest" was a riddle there (the page-by-page
  // review of 2026-10-11).
  return (
    <Term tip={sentence} name={rankWords(cue.rank, cue.of)} className="fsp-matchup-term">
      <span
        className="fsp-matchup-chip fsp-num"
        data-band={cue.band}
        data-early={cue.games < MATCHUP_EARLY_GAMES || undefined}
      >
        {rankWords(cue.rank, cue.of)}
      </span>
    </Term>
  );
}
